import { isUsdLikeCurrency } from '@/lib/fee-format'
import { isInverseInstrument } from '@/lib/instrument-util'
import { LiquidityType, OrderSide, type Trade } from '@/types/generated/common/trading'
import { type Exchange, type Instrument, InstrumentKind } from '@/types/generated/common/types'

/**
 * The user's own trading, from their fills: volume over time and by venue, maker share, fees
 * paid, and two estimates of what OrderX saved them —
 * - fee savings: maker fills paid the maker rate instead of the taker rate;
 * - spread capture: each fill's price against the touch at that moment (Trade.benchmark), i.e.
 *   what crossing the spread would have cost instead.
 * Pure (tested in __tests__/home-trade-stats.test.ts). USD values only count instruments
 * quoted in USD-like currencies; options are counted in contracts, not notional.
 */

export interface FeeRates {
  maker: number
  taker: number
}

export interface TradeStatsInput {
  trades: readonly Trade[]
  instrumentOf: (trade: Trade) => Instrument | undefined
  /** The account's maker/taker rates for the trade's instrument, as fractions. */
  feeRatesOf: (trade: Trade, instrument: Instrument | undefined) => FeeRates | null
  /** Window start (epoch ms); fills before it are ignored. */
  from: number
  now: number
  /** Number of daily buckets ending today. */
  days: number
}

export interface TradeStats {
  volumeUsd: number
  fillCount: number
  optionContracts: number
  /** Share of the USD volume filled as maker (0–1); null with no volume. */
  makerShare: number | null
  daily: { dayMs: number; volumeUsd: number }[]
  byVenue: { exchange: Exchange; volumeUsd: number }[]
  feesPaidUsd: number
  feeSavingsUsd: number
  spreadCaptureUsd: number
  /** Fills that had a benchmark to measure spread capture against. */
  benchmarkedFills: number
  /** The most traded instruments by USD volume, with their maker share. */
  topInstruments: { symbol: string; exchange: Exchange; volumeUsd: number; makerShare: number }[]
  /** The fills that beat the touch by the most (USD). */
  bestFills: { id: string; symbol: string; side: 'buy' | 'sell'; capturedUsd: number; at: number }[]
}

const DAY_MS = 86_400_000

function utcDay(ms: number): number {
  return Math.floor(ms / DAY_MS) * DAY_MS
}

function num(value: string | undefined): number | null {
  if (value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function quoteIsUsd(trade: Trade, instrument: Instrument | undefined): boolean {
  if (instrument?.quoteCurrency) return isUsdLikeCurrency(instrument.quoteCurrency)
  return /USD/i.test(trade.symbol) || /PERPETUAL$/i.test(trade.symbol)
}

/** A fill's size: base units and USD notional (null when not USD-quoted or an option). */
export function fillNotional(
  trade: Trade,
  instrument: Instrument | undefined
): { base: number; usd: number | null } | null {
  const quantity = Math.abs(num(trade.quantity) ?? 0)
  const price = num(trade.price) ?? 0
  if (quantity === 0 || price <= 0) return null
  if (instrument?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) return null
  // Inverse contracts are sized in USD; linear ones in the base coin.
  if (isInverseInstrument(trade.symbol, instrument)) {
    return { base: quantity / price, usd: quantity }
  }
  return { base: quantity, usd: quoteIsUsd(trade, instrument) ? quantity * price : null }
}

/** The fee in USD: as is when charged in a USD-like coin, × price when charged in the base. */
export function feeUsd(trade: Trade): number | null {
  const fee = num(trade.fee)
  if (fee === null) return null
  if (isUsdLikeCurrency(trade.feeCurrency)) return fee
  const price = num(trade.price)
  if (price === null || !trade.feeCurrency) return null
  return trade.symbol.toUpperCase().startsWith(trade.feeCurrency.toUpperCase()) ? fee * price : null
}

/**
 * What the fill gained against crossing the spread at that moment: a buy below the ask, a
 * sell above the bid. Negative when it filled worse than the touch (a market order walking
 * the book). Null without a benchmark.
 */
export function spreadCaptureUsd(trade: Trade, baseQuantity: number): number | null {
  const price = num(trade.price)
  const bid = num(trade.benchmark?.bid)
  const ask = num(trade.benchmark?.ask)
  if (price === null) return null
  if (trade.side === OrderSide.ORDER_SIDE_BUY && ask !== null && ask > 0) {
    return (ask - price) * baseQuantity
  }
  if (trade.side === OrderSide.ORDER_SIDE_SELL && bid !== null && bid > 0) {
    return (price - bid) * baseQuantity
  }
  return null
}

export function computeTradeStats(input: TradeStatsInput): TradeStats {
  const { trades, instrumentOf, feeRatesOf, from, now, days } = input
  const today = utcDay(now)
  const firstDay = today - (days - 1) * DAY_MS
  const daily = new Map<number, number>()
  for (let day = firstDay; day <= today; day += DAY_MS) daily.set(day, 0)
  const byVenue = new Map<Exchange, number>()

  let volumeUsd = 0
  let makerUsd = 0
  let fillCount = 0
  let optionContracts = 0
  let feesPaidUsd = 0
  let feeSavingsUsd = 0
  let spreadCapture = 0
  let benchmarkedFills = 0
  const instruments = new Map<
    string,
    { symbol: string; exchange: Exchange; volume: number; maker: number }
  >()
  const captures: TradeStats['bestFills'] = []

  for (const trade of trades) {
    const at = num(trade.executedAt) ?? 0
    if (at < from || at > now) continue
    fillCount += 1
    const instrument = instrumentOf(trade)
    const fee = feeUsd(trade)
    if (fee !== null) feesPaidUsd += fee

    if (instrument?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION) {
      optionContracts += Math.abs(num(trade.quantity) ?? 0)
      continue
    }
    const size = fillNotional(trade, instrument)
    if (!size) continue

    const captured = spreadCaptureUsd(trade, size.base)
    if (captured !== null && quoteIsUsd(trade, instrument)) {
      spreadCapture += captured
      benchmarkedFills += 1
      if (captured > 0) {
        captures.push({
          id: trade.id,
          symbol: trade.symbol,
          side: trade.side === OrderSide.ORDER_SIDE_SELL ? 'sell' : 'buy',
          capturedUsd: captured,
          at,
        })
      }
    }
    if (size.usd === null) continue

    volumeUsd += size.usd
    const day = utcDay(at)
    if (daily.has(day)) daily.set(day, (daily.get(day) ?? 0) + size.usd)
    byVenue.set(trade.exchange, (byVenue.get(trade.exchange) ?? 0) + size.usd)
    const key = `${trade.exchange}:${trade.symbol}`
    const entry = instruments.get(key) ?? {
      symbol: trade.symbol,
      exchange: trade.exchange,
      volume: 0,
      maker: 0,
    }
    entry.volume += size.usd
    if (trade.liquidityType === LiquidityType.LIQUIDITY_TYPE_MAKER) entry.maker += size.usd
    instruments.set(key, entry)

    if (trade.liquidityType === LiquidityType.LIQUIDITY_TYPE_MAKER) {
      makerUsd += size.usd
      const rates = feeRatesOf(trade, instrument)
      if (rates && rates.taker > rates.maker)
        feeSavingsUsd += size.usd * (rates.taker - rates.maker)
    }
  }

  return {
    volumeUsd,
    fillCount,
    optionContracts,
    makerShare: volumeUsd > 0 ? makerUsd / volumeUsd : null,
    daily: [...daily.entries()].map(([dayMs, value]) => ({ dayMs, volumeUsd: value })),
    byVenue: [...byVenue.entries()]
      .map(([exchange, value]) => ({ exchange, volumeUsd: value }))
      .sort((a, b) => b.volumeUsd - a.volumeUsd),
    feesPaidUsd,
    feeSavingsUsd,
    spreadCaptureUsd: spreadCapture,
    benchmarkedFills,
    topInstruments: [...instruments.values()]
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 5)
      .map((entry) => ({
        symbol: entry.symbol,
        exchange: entry.exchange,
        volumeUsd: entry.volume,
        makerShare: entry.volume > 0 ? entry.maker / entry.volume : 0,
      })),
    bestFills: captures.sort((a, b) => b.capturedUsd - a.capturedUsd).slice(0, 3),
  }
}
